import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Modal,
  SimpleSelect,
  TextAreaInput,
  useToast,
} from 'corso-admin-ui';
import { ClaimStatusEnum, crewMerchantShippingClaimApi } from 'corso-types';
import { Controller, useForm } from 'react-hook-form';
import { shippingClaimStatusName } from '~/utils/enumNameMaps';
import { isErrorMessage } from '~/utils/errorResponse';
import {
  ShippingClaimClose,
  shippingClaimCloseStatuses,
  useShippingClaimClose,
} from '../hooks/useShippingClaimClose';

const closeSchema =
  crewMerchantShippingClaimApi[
    '/:storeId/shipping-protection/claim/:shippingClaimId/close'
  ].post.request.body;

export function CloseShippingClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { mutate: closeClaim, isPending } = useShippingClaimClose();

  const formId = 'close-shipping-claim-form';
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(closeSchema),
    defaultValues: {
      status: ClaimStatusEnum.closedResolved,
      comment: '',
    } satisfies ShippingClaimClose,
  });

  const closeAndReset = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      title="Close Claim"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="caution"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Close Claim
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit((data) =>
            closeClaim(data, {
              onSuccess: closeAndReset,
              onError: (error) =>
                toast.show(
                  isErrorMessage(error)
                    ? error.message
                    : 'Could not close this claim. Please try again.',
                ),
            }),
          )(e).catch((error: unknown) =>
            console.error('Failed to close shipping claim', error),
          );
        }}
        className="flex flex-col gap-4"
      >
        <Alert heading="Did you know?" variant="tip">
          A closed claim can be restored in the future if needed.
        </Alert>
        <Controller
          control={control}
          name="status"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Status"
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
              options={shippingClaimCloseStatuses.map((value) => ({
                value,
                label: shippingClaimStatusName[value],
              }))}
            />
          )}
        />
        <TextAreaInput
          id="close-shipping-claim-comment"
          label="Comment"
          details="This comment will be added to the timeline."
          rows={3}
          {...register('comment')}
          error={errors.comment?.message}
        />
      </form>
    </Modal>
  );
}
